/**
 * Tiny localStorage-backed tracker for the in-flight bulk resume import.
 *
 * Why this exists: the ingest pipeline runs server-side, independent of the
 * browser. Before this, the only progress UI was local React state on the
 * BulkResumeImport page — navigate away or refresh and you lost all sight
 * of a running import. This persists the active batch so a global indicator
 * (BulkImportDock) can show "an import is running" on every page, and the
 * import page itself can restore progress after a reload.
 *
 * Single active batch at a time (the import form blocks a second upload
 * while one is in flight). Cleared when the batch finishes or is dismissed.
 */
import { useEffect, useState } from 'react';

export interface ActiveBulkImport {
  batchId: string;
  filesTotal: number;
  startedAt: number; // Date.now() at upload
}

const KEY = 'prism.activeBulkImport';
// Same-tab change notification (the native 'storage' event only fires in
// OTHER tabs, so we dispatch our own for the current tab).
const EVENT = 'prism:bulkimport-changed';

export function getActiveBulkImport(): ActiveBulkImport | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ActiveBulkImport) : null;
  } catch {
    return null;
  }
}

export function setActiveBulkImport(value: ActiveBulkImport): void {
  localStorage.setItem(KEY, JSON.stringify(value));
  window.dispatchEvent(new Event(EVENT));
}

export function clearActiveBulkImport(): void {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new Event(EVENT));
}

/** Reactive read — re-renders the caller whenever the active import changes
 *  (this tab or another). */
export function useActiveBulkImport(): ActiveBulkImport | null {
  const [value, setValue] = useState<ActiveBulkImport | null>(getActiveBulkImport);
  useEffect(() => {
    const onChange = () => setValue(getActiveBulkImport());
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return value;
}
